import React, { useState, useEffect, useRef } from "react";
import { AxiosGetMethod, AxiosPutMethod } from "../helpers/customAxios";
import { ErrorToast, SuccessToast } from "./toastify";
import { FaTimes, FaSave, FaSpinner, FaGripVertical } from "react-icons/fa";

export default function ThaaiMamanLineUpModal({ onClose }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const dragItem = useRef();
  const dragOverItem = useRef();
  const [dragOverIndex, setDragOverIndex] = useState(null);

  useEffect(() => {
    fetchThaaiMamanList();
  }, []);

  const fetchThaaiMamanList = async () => {
    setLoading(true);
    try {
      const res = await AxiosGetMethod("entry/thaai-maman/list");
      if (res?.status === 200) {
        setEntries(res.data?.data || []);
      } else {
        ErrorToast("Failed to fetch Thaai Maman list");
      }
    } catch (error) {
      console.error(error);
      ErrorToast("Error fetching Thaai Maman list");
    } finally {
      setLoading(false);
    }
  };

  const handleDragStart = (e, index) => {
    dragItem.current = index;
    // Set some styles or data if needed, but not strictly necessary for simple reordering
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragEnter = (e, index) => {
    e.preventDefault();
    dragOverItem.current = index;
    setDragOverIndex(index);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };
  
  const handleDragEnd = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (dragItem.current !== null && dragOverItem.current !== null && dragItem.current !== dragOverItem.current) {
        const _entries = [...entries];
        const draggedItemContent = _entries.splice(dragItem.current, 1)[0];
        _entries.splice(dragOverItem.current, 0, draggedItemContent);
        setEntries(_entries);
    }
    dragItem.current = null;
    dragOverItem.current = null;
    setDragOverIndex(null);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const orders = entries.map((entry, idx) => ({
        entry_id: entry.entry_id,
        order: idx + 1, // Order is simply based on their position in the array
      }));

      const res = await AxiosPutMethod("entry/thaai-maman/bulk-order", { orders });
      if (res?.status === 200) {
        SuccessToast("Thaai Maman Line Up saved successfully");
        onClose(true); // pass true to indicate it was saved
      } else {
        ErrorToast("Failed to save Thaai Maman Line Up");
      }
    } catch (error) {
      console.error(error);
      ErrorToast("Error saving Thaai Maman Line Up");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="w-full max-w-3xl rounded-2xl bg-gray-900 shadow-2xl border border-gray-700 flex flex-col max-h-[90vh]">
        
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-800 px-6 py-4">
          <h2 className="text-lg font-bold text-white">Thaai Maman Line Up</h2>
          <button
            onClick={() => onClose(false)}
            className="text-gray-400 hover:text-white transition"
          >
            <FaTimes size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex justify-center items-center py-10">
              <FaSpinner className="animate-spin text-emerald-500" size={24} />
              <span className="ml-3 text-gray-400 text-sm">Loading entries...</span>
            </div>
          ) : entries.length === 0 ? (
            <div className="text-center py-10 text-gray-500">
              No Thaai Maman entries found for this event.
            </div>
          ) : (
            <div className="space-y-2">
              <div className="grid grid-cols-[40px_1fr_1fr_100px] gap-4 px-4 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider border-b border-gray-800">
                <div></div>
                <div>Name</div>
                <div>Location</div>
                <div className="text-right">Amount</div>
              </div>
              {entries.map((entry, index) => (
                <React.Fragment key={entry.entry_id}>
                  {dragOverIndex === index && dragItem.current !== index && (
                    <div className="h-1 bg-emerald-500 rounded-full shadow-[0_0_8px_rgba(16,185,129,0.8)] my-1 pointer-events-none transition-all duration-200" />
                  )}
                  <div
                    draggable
                    onDragStart={(e) => handleDragStart(e, index)}
                    onDragEnter={(e) => handleDragEnter(e, index)}
                    onDragOver={handleDragOver}
                    onDragEnd={handleDragEnd}
                    onDrop={handleDrop}
                    className={`grid grid-cols-[40px_1fr_1fr_100px] items-center gap-4 rounded-xl px-4 py-3 transition cursor-grab active:cursor-grabbing ${
                      dragItem.current === index ? 'opacity-40 bg-gray-900 border border-gray-800' : 'bg-gray-800/50 border border-gray-700 hover:bg-gray-700/80'
                    }`}
                  >
                  <div className="flex items-center justify-center text-gray-500">
                    <FaGripVertical />
                    <span className="ml-2 font-bold text-gray-400">{index + 1}</span>
                  </div>
                  <div className="min-w-0">
                    <p className="truncate font-bold text-white">{entry.name || "-"}</p>
                  </div>
                  <div className="min-w-0">
                    <p className="truncate text-sm text-gray-400">
                      {[entry.village, entry.city].filter(Boolean).join(", ") || "-"}
                    </p>
                  </div>
                  <div className="text-right font-bold text-emerald-400">
                    ₹{Number(entry.amount).toLocaleString("en-IN")}
                  </div>
                  </div>
                </React.Fragment>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-800 px-6 py-4 flex justify-end gap-3 bg-gray-900/80 rounded-b-2xl">
          <button
            type="button"
            onClick={() => onClose(false)}
            className="px-4 py-2 text-sm font-semibold text-gray-300 hover:text-white transition"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || loading}
            className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-5 py-2 text-sm font-bold text-white shadow hover:bg-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed transition"
          >
            {saving && <FaSpinner className="animate-spin" />}
            Save Line Up
          </button>
        </div>
      </div>
    </div>
  );
}
